import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import products from "../data/products";

const PROJECTS = [
  {
    title: "Textile Manufacturing Automation",
    client: "Ramesh Textiles, Ludhiana",
    productSlug: "erp",
    tag: "Manufacturing",
    summary:
      "End-to-end ERP for inventory, raw materials, and production tracking. Reduced manual input by 60%.",
  },
  {
    title: "Retail CRM + POS Integration",
    client: "North Star Retail, Delhi NCR",
    productSlug: "erp",
    tag: "Retail",
    summary:
      "Integrated Byteflow CRM with their existing POS, improving customer retention and real-time inventory sync.",
  },
  {
    title: "Payroll & Compliance System",
    client: "Delta Electronics, Bengaluru",
    productSlug: "erp",
    tag: "Electronics",
    summary:
      "Payroll + tax module with automated reporting. Saved 30 hours/month in HR time.",
  },
  {
    title: "Last-mile Delivery Rollout",
    client: "Swift Couriers, Mumbai",
    productSlug: "courier",
    tag: "Logistics",
    summary:
      "Driver app and COD reconciliation across 12 branches. Cut daily settlement time by 70%.",
  },
  {
    title: "Multi-branch Pickup Network",
    client: "Express Cargo, Pune",
    productSlug: "courier",
    tag: "Logistics",
    summary:
      "Real-time tracking and route optimisation across 8 branches. Improved on-time delivery rate to 96%.",
  },
  {
    title: "Multi-Store Pharmacy Rollout",
    client: "MedPlus Pharmacy, Jaipur",
    productSlug: "medical",
    tag: "Pharmacy",
    summary:
      "GST billing, batch & expiry tracking across 6 retail outlets with central stock visibility.",
  },
  {
    title: "MR Field Force Automation",
    client: "Aushadhi Pharma, Lucknow",
    productSlug: "medical",
    tag: "Pharma",
    summary:
      "Rolled out the MR App for 80+ field reps: call reporting, order booking, and geo-tagged visits.",
  },
  {
    title: "Admissions to Parent App",
    client: "Greenfield Academy, Gurgaon",
    productSlug: "school",
    tag: "Education",
    summary:
      "Replaced four legacy tools with one platform: admissions, attendance, fees, and a parent app.",
  },
  {
    title: "Multi-campus Fee Collection",
    client: "Sunrise Schools, Indore",
    productSlug: "school",
    tag: "Education",
    summary:
      "Online fee collection across 3 campuses with auto-reminders. Reduced overdue fees by 45%.",
  },
  {
    title: "D2C Store Launch",
    client: "Herbal Roots, Bengaluru",
    productSlug: "ecommerce",
    tag: "E-commerce",
    summary:
      "Custom storefront with ERP-synced inventory and multi-gateway checkout. Live in 5 weeks.",
  },
];

const FILTERS = [
  { value: "all", label: "All" },
  ...products.map((p) => ({ value: p.slug, label: p.shortName })),
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const Projects = () => {
  const [filter, setFilter] = useState("all");

  const filtered = useMemo(
    () =>
      filter === "all"
        ? PROJECTS
        : PROJECTS.filter((p) => p.productSlug === filter),
    [filter]
  );

  const productBySlug = useMemo(() => {
    const map = {};
    for (const p of products) map[p.slug] = p;
    return map;
  }, []);

  return (
    <>
      <section className="page-hero">
        <motion.div
          className="page-hero__inner"
          initial="hidden"
          animate="visible"
          variants={fadeUp}
        >
          <p className="eyebrow">Case studies</p>
          <h1>Recent implementations</h1>
          <p className="page-hero__lead">
            Byteflow has helped streamline operations for small and mid-sized
            businesses across industries.
          </p>
        </motion.div>
      </section>

      <section className="section">
        <div className="filter-pills" role="tablist">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              role="tab"
              aria-selected={filter === f.value}
              className={"filter-pill" + (filter === f.value ? " is-active" : "")}
              onClick={() => setFilter(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="projects-grid">
          {filtered.map((p, i) => {
            const product = productBySlug[p.productSlug];
            return (
              <motion.article
                key={p.title}
                className="project-card"
                style={{ "--product-accent": product?.accent }}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
              >
                <div className="project-card__tags">
                  {product && (
                    <Link
                      to={`/products/${product.slug}`}
                      className="project-card__product"
                    >
                      {product.icon} {product.shortName}
                    </Link>
                  )}
                  <span className="project-card__tag">{p.tag}</span>
                </div>
                <h3>{p.title}</h3>
                <p className="project-card__client">{p.client}</p>
                <p>{p.summary}</p>
              </motion.article>
            );
          })}
        </div>
      </section>

      <section className="cta-strip">
        <div className="cta-strip__inner">
          <div>
            <h2>Got a project in mind?</h2>
            <p>Let’s talk about what Byteflow can do for your team.</p>
          </div>
          <Link to="/contact" className="btn-primary btn-primary--inverse">
            Let’s Build Yours
          </Link>
        </div>
      </section>
    </>
  );
};

export default Projects;
